import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useCompany } from "./CompanyContext";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

const DepartmentContext = createContext();

export const useDepartments = () => {
  const context = useContext(DepartmentContext);
  if (!context) {
    throw new Error("useDepartments must be used within a DepartmentProvider");
  }
  return context;
};

export const DepartmentProvider = ({ children }) => {
  const [departments, setDepartments] = useState([]);
  const [subdepartments, setSubdepartments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { companyInfo } = useCompany();

  // Create axios instance with default config
  const api = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Fetch all departments
  const fetchDepartments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/departments");
      setDepartments(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      console.error("Error fetching departments:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subdepartments for a department
  const fetchSubdepartments = async (departmentId) => {
    try {
      const response = await api.get(
        `/departments/${departmentId}/subdepartments`
      );
      setSubdepartments((prev) => ({
        ...prev,
        [departmentId]: response.data,
      }));
      return response.data;
    } catch (err) {
      console.error(
        `Error fetching subdepartments for department ${departmentId}:`,
        err
      );
      throw err.response?.data?.message || err.message;
    }
  };

  // Get departments by organization
  const getDepartmentsByOrganization = async (organizationId) => {
    try {
      const response = await api.get(
        `/departments/organization/${organizationId}`
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching departments by organization:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Get departments by branch
  const getDepartmentsByBranch = async (branchId) => {
    try {
      const response = await api.get(`/departments/branch/${branchId}`);
      return response.data;
    } catch (err) {
      console.error("Error fetching departments by branch:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Create new department
  const createDepartment = async (departmentData) => {
    try {
      const response = await api.post("/departments", departmentData);
      const newDepartment = response.data;
      setDepartments((prev) => [...prev, newDepartment]);
      return newDepartment;
    } catch (err) {
      console.error("Error creating department:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Update department
  const updateDepartment = async (id, departmentData) => {
    try {
      const response = await api.put(`/departments/${id}`, departmentData);
      const updatedDepartment = response.data;
      setDepartments((prev) =>
        prev.map((dept) => (dept.id === id ? updatedDepartment : dept))
      );
      return updatedDepartment;
    } catch (err) {
      console.error("Error updating department:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Delete department
  const deleteDepartment = async (id) => {
    try {
      await api.delete(`/departments/${id}`);
      setDepartments((prev) => prev.filter((dept) => dept.id !== id));
      setSubdepartments((prev) => {
        const newSubdepartments = { ...prev };
        delete newSubdepartments[id];
        return newSubdepartments;
      });
    } catch (err) {
      console.error("Error deleting department:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Create subdepartment
  const createSubdepartment = async (departmentId, subdepartmentData) => {
    try {
      const response = await api.post(
        `/departments/${departmentId}/subdepartments`,
        subdepartmentData
      );
      const newSubdepartment = response.data;
      setSubdepartments((prev) => ({
        ...prev,
        [departmentId]: [...(prev[departmentId] || []), newSubdepartment],
      }));
      return newSubdepartment;
    } catch (err) {
      console.error("Error creating subdepartment:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Update subdepartment
  const updateSubdepartment = async (
    departmentId,
    subdepartmentId,
    subdepartmentData
  ) => {
    try {
      const response = await api.put(
        `/departments/${departmentId}/subdepartments/${subdepartmentId}`,
        subdepartmentData
      );
      const updatedSubdepartment = response.data;
      setSubdepartments((prev) => ({
        ...prev,
        [departmentId]: prev[departmentId].map((subdept) =>
          subdept.id === subdepartmentId ? updatedSubdepartment : subdept
        ),
      }));
      return updatedSubdepartment;
    } catch (err) {
      console.error("Error updating subdepartment:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Delete subdepartment
  const deleteSubdepartment = async (departmentId, subdepartmentId) => {
    try {
      await api.delete(
        `/departments/${departmentId}/subdepartments/${subdepartmentId}`
      );
      setSubdepartments((prev) => ({
        ...prev,
        [departmentId]: prev[departmentId].filter(
          (subdept) => subdept.id !== subdepartmentId
        ),
      }));
    } catch (err) {
      console.error("Error deleting subdepartment:", err);
      throw err.response?.data?.message || err.message;
    }
  };

  // Initial fetch when company changes
  useEffect(() => {
    if (companyInfo?.id) {
      fetchDepartments();
    }
  }, [companyInfo?.id]);

  const value = {
    departments,
    subdepartments,
    loading,
    error,
    fetchDepartments,
    fetchSubdepartments,
    getDepartmentsByOrganization,
    getDepartmentsByBranch,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    createSubdepartment,
    updateSubdepartment,
    deleteSubdepartment,
  };

  return (
    <DepartmentContext.Provider value={value}>
      {children}
    </DepartmentContext.Provider>
  );
};
